import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import { Lines } from "@/components/marketing/Lines";
import { BODY, HEADING_2_ON_H3 } from "@/components/marketing/marketingType";
import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { Laid } from "@/content/landing/sectionLayout";
import HeroSocialProof from "../HeroSocialProof";
import { BoldRuns, LandingSection } from "../LandingPrimitives";
import { sectionPad } from "./landingRun";
import { Closer } from "./PpcBlocks";

type PpcHero = Laid<"ppcHero">;
type Tile = NonNullable<PpcHero["tiles"]>[number];

/** `.ppc-visual_logo`: the green box's own wordmark, which stands in for the bar. */
const BOX_WORDMARK = "/marketing/campaign/ppc-logo.png";

/** `.ppc-visual_box`: the green box the hero is set in, its logo, words and tiles 48 apart. */
const GREEN_BOX = {
  display: "flex",
  flexDirection: "column",
  gap: "48px",
  p: "80px 32px 32px",
  borderRadius: "32px",
  backgroundColor: "#E7F5E8",
  [NOT_WIDE]: { gap: "24px", p: "40px 24px" },
  [TINY]: { p: "24px 16px" },
} satisfies SxProps<Theme>;

/**
 * `.ppc-hero_wrap`: the white hero, 48 over and under its words (32 on a
 * phone). Published as margins inside `.w-container`, whose clearfix keeps
 * them from collapsing into the blocks around it, so padding here.
 */
const PLAIN_HERO = {
  display: "flex",
  flexDirection: "column",
  py: "48px",
  [TINY]: { py: "32px" },
} satisfies SxProps<Theme>;

/** `.ppc-hero-header_wrap` and `.header-hp_top`: the proof row over the title, centred. */
function headerGap(block: PpcHero) {
  return block.box ? "32px" : "12.8px";
}

/** The proof row, the title and its line: `.ppc-hero-header_wrap` or `.header-hp_top`. */
function Header({ block }: { block: PpcHero }) {
  const lead = block.level === 1;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: headerGap(block),
        textAlign: "center",
        maxWidth: 1,
      }}
    >
      {/* In the green box the row is `.hero-proof_list.is-new`. */}
      {block.socialProof ? (
        <HeroSocialProof
          label={block.socialProof}
          inline
          trustpilot={!block.proofPicture}
          ratings={block.proofPicture}
          stackFromCard={block.box}
        />
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12.8px",
          width: 1,
          maxWidth: block.wideTitle ? "81ch" : "69ch",
        }}
      >
        {/* Further down a page the title is `h3.h2_new`, as published. */}
        <Typography
          variant={lead ? "h1" : undefined}
          component={lead ? "h1" : "h3"}
          sx={lead ? { color: marketingPalette.ink } : HEADING_2_ON_H3}
        >
          <Lines text={block.title} />
        </Typography>
        {block.body ? (
          <Typography sx={BODY}>
            <BoldRuns text={block.body} />
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

/** `.hero-visual-img`: the tile's picture, and `.is-m` in its place below the card step. */
function TilePicture({ tile }: { tile: Tile }) {
  const picture = {
    display: "block",
    flex: 1,
    width: 1,
    maxWidth: "100%",
  } satisfies SxProps<Theme>;
  if (!tile.mobilePicture) {
    return tile.picture ? (
      <Box
        component="img"
        src={tile.picture}
        alt=""
        loading="lazy"
        sx={picture}
      />
    ) : null;
  }
  return (
    <>
      {tile.picture ? (
        <Box
          component="img"
          src={tile.picture}
          alt=""
          loading="lazy"
          sx={[picture, { [MID]: { display: "none" } }]}
        />
      ) : null}
      <Box
        component="img"
        src={tile.mobilePicture}
        alt=""
        loading="lazy"
        sx={[picture, { display: "none", [MID]: { display: "block" } }]}
      />
    </>
  );
}

/** `.ppc-visual-item`: a tile of the green box, its title over its picture. */
function BoxTile({ tile }: { tile: Tile }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        p: "16px",
        borderRadius: "8px",
        backgroundColor: "common.white",
        [TINY]: { gap: "12px", p: "12px", alignItems: "center" },
      }}
    >
      <Box
        sx={{
          color: marketingPalette.ink,
          fontSize: 19,
          lineHeight: "28.5px",
          WebkitTextStroke: "0.7px",
          [TINY]: { fontSize: 16, lineHeight: "20px" },
        }}
      >
        {tile.title}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 1,
        }}
      >
        <TilePicture tile={tile} />
      </Box>
    </Box>
  );
}

/**
 * `.hero-visual_list`: the green box's tiles, three to the first row and two
 * to the second, two across below the wide step and one column below the
 * card step. `.is-hide` drops them there instead.
 */
function BoxTiles({
  tiles,
  hidden,
}: {
  tiles: ReadonlyArray<Tile>;
  hidden?: boolean;
}) {
  const rows = [tiles.slice(0, 3), tiles.slice(3)].filter((row) => row.length);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        [MID]: hidden ? { display: "none" } : {},
        [TINY]: { gap: "8px" },
      }}
    >
      {rows.map((row, index) => (
        <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${row.length}, 1fr)`,
            gap: "12px",
            [NOT_WIDE]: { gridTemplateColumns: "1fr 1fr" },
            [MID]: { display: "flex", flexDirection: "column" },
            [TINY]: { gap: "8px" },
          }}
        >
          {row.map((tile) => (
            <BoxTile key={tile.title} tile={tile} />
          ))}
        </Box>
      ))}
    </Box>
  );
}

/** `.ppc-hero-visual_list` on its grey strip: the white hero's tiles, as many 16rem as fit. */
function StripTiles({ tiles }: { tiles: ReadonlyArray<Tile> }) {
  return (
    <LandingSection padding="none" ground="grey">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fill, minmax(min(100%, 16rem), 1fr))",
          gap: "20px",
          // Margins inside `.w-container`, which keeps them: the strip's
          // grey runs 24 past the tiles either way.
          py: "24px",
          [TINY]: { gap: "16px" },
        }}
      >
        {tiles.map((tile) => (
          <Box
            key={tile.title}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              p: "16px",
              borderRadius: "8px",
              backgroundColor: "common.white",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                color: marketingPalette.ink,
                fontSize: 19,
                lineHeight: "28.5px",
                WebkitTextStroke: "0.5px",
                [MID]: { fontSize: 17, lineHeight: "25.5px" },
              }}
            >
              {tile.title}
            </Box>
            {tile.picture ? (
              <Box
                component="img"
                src={tile.picture}
                alt=""
                loading="lazy"
                sx={{ display: "block", maxWidth: "100%" }}
              />
            ) : null}
          </Box>
        ))}
      </Box>
    </LandingSection>
  );
}

/**
 * The paid-search pages' hero: the proof row, the title, the button and its
 * line, and the tiles, set in the green box with its own wordmark or white
 * over a grey strip. The same block, with no button and a level-2 title,
 * heads a section further down a page.
 */
export default function PpcHeroBlock({ block }: { block: PpcHero }) {
  const tiles = block.tiles ?? [];
  if (block.box) {
    return (
      <LandingSection padding={block.pad ? sectionPad(block.pad) : "top"}>
        <Box sx={GREEN_BOX}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="img"
              src={BOX_WORDMARK}
              alt="Webshare"
              sx={{ display: "block", width: "210px" }}
            />
          </Box>
          <Header block={block} />
          <Closer
            ctas={block.ctas}
            notes={block.reassurances}
            quiet={block.box}
          />
          {tiles.length ? (
            <BoxTiles tiles={tiles} hidden={block.tilesHidden} />
          ) : null}
        </Box>
      </LandingSection>
    );
  }
  return (
    <>
      <LandingSection padding={block.pad ? sectionPad(block.pad) : "none"}>
        <Box sx={[PLAIN_HERO, { gap: "12.8px", alignItems: "center" }]}>
          <Header block={block} />
          <Closer
            ctas={block.ctas}
            notes={block.reassurances}
            quiet={block.box}
          />
        </Box>
      </LandingSection>
      {tiles.length ? <StripTiles tiles={tiles} /> : null}
    </>
  );
}
